<template>
    <div @click.stop="handleClick">
        <multiselect
            ref="multiselect"
            v-model="selectValue"
            class="dk-select"
            :class="{ 'has-clear-button': showClearOptionsButton }"
            :label="areOptionsObjects ? propertyLabel : null"
            :track-by="areOptionsObjects ? propertyTrackBy : null"
            :placeholder="placeholder"
            :disabled="isDisabled"
            :multiple="multiple"
            :searchable="false"
            :options="options"
            :select-label="null"
            :deselect-label="null"
            :selected-label="null"
            :close-on-select="!multiple"
            :limit="optionsToDisplayLimit"
            :limit-text="limitText"
            :allow-empty="multiple ? true : false"
            @input="handleUpdate"
            @close="handleOnClose"
        >
            <template #option="{ option }">
                <span v-if="multiple" class="tw-w-full !tw-flex tw-items-center">
                    <input
                        class="tw-pointer-events-none"
                        type="checkbox"
                        :checked="getCheckedOptionStatus(option)"
                        autocomplete="off"
                    >
                    {{ getOptionLabel(option) }}
                </span>
                <dk-dealer-choice-card
                    v-else-if="isDealerSelect"
                    :dealer-name="option.name"
                    :dealer-address="option.address"
                    :dealer-distance="option.distance"
                    :is-selected="isCurrentDealer(option.locationId)"
                    is-select-option
                />
                <span v-else class="tw-w-full !tw-flex">
                    {{ getOptionLabel(option) }}
                </span>
            </template>
            <template v-if="multiple" #clear>
                <dk-button
                    v-if="showClearOptionsButton"
                    class="dk-select__clear tw-absolute tw-right-0 tw-top-1/2 -tw-translate-y-1/2 !tw-w-12 !tw-h-12 tw-p-3 tw-flex tw-items-center tw-justify-center tw-cursor-pointer"
                    type="button"
                    is-icon-only
                    variant="ghost"
                    @click.prevent.stop="clearOptions()"
                >
                    <dk-icon name="ds-close-empty" size="xs" color="basic" />
                </dk-button>
            </template>
            <template v-if="hasApplyButton && multiple" #afterList>
                <div class="tw-w-full tw-p-[2px] tw-pl-3 tw-flex tw-items-center tw-justify-between">
                    <dk-button :variant="'subtle'" @click.prevent="handleClearAll">
                        {{ textClearAll }}
                    </dk-button>
                    <dk-button class="!tw-h-12" @click.prevent="handleApplyChanges">
                        {{ textApply }}
                    </dk-button>
                </div>
            </template>
            <template v-if="isDealerSelect" #noOptions>
                <span v-if="isNoResult">{{ $t('lead-form.dealer-selection.no-results') }}</span>
                <span v-if="loading">{{ $t('lead-form.dealer-selection.loading-results') }}</span>
            </template>
        </multiselect>
    </div>
</template>

<script>
import DkButton from '@v4/common/components/atoms/DkButton.vue';
import Multiselect from 'vue-multiselect';

export default {
    name: 'DkSelect',
    components: {
        Multiselect,
        DkButton,
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        DkDealerChoiceCard: () => ({
            component: import(
                /* webpackChunkName: "DkDealerChoiceCard-vue" */ '@v4/common/components/organisms/DealerSelection/DkDealerChoiceCard.vue'
            ),
        }),
    },
    props: {
        /**
         * The initial value that the multiselect receives
         */
        value: {
            type: [Array, Object, String],
            default: null,
        },
        /**
         * Options for the multiselect
         */
        options: {
            type: Array,
            required: true,
        },
        /**
         * Flag for disabled state
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Placeholder text
         */
        placeholder: {
            type: String,
            default: null,
        },
        /**
         * Flag for multiple selection
         */
        multiple: {
            type: Boolean,
            default: false,
        },
        /**
         * Flag for showing the "Clear all" and "Apply" buttons
         */
        hasApplyButton: {
            type: Boolean,
            default: false,
        },
        /**
         * Text for the Clear All button
         */
        textClearAll: {
            type: String,
            default: '',
        },
        /**
         * Text for the Apply button
         */
        textApply: {
            type: String,
            default: '',
        },
        /**
         * Object property to use for the label of the option
         */
        propertyLabel: {
            type: String,
            default: null,
        },
        /**
         * Object property to use for the value of the option
         */
        propertyTrackBy: {
            type: String,
            default: null,
        },
        optionsToDisplayLimit: {
            type: Number,
            default: 2,
        },
        /*
         * Flag for custom options template (e.g. dealer selection)
         */
        isDealerSelect: {
            type: Boolean,
            default: false,
        },
        /*
         * Show/hide the loading spinner.
         */
        isLoading: {
            type: Boolean,
            default: false,
        },
        /*
         *
         */
        hasNoResult: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectValue: this.value,
            hasAppliedChanges: false,
            isDisabled: this.disabled,
            loading: this.isLoading,
            isNoResult: this.hasNoResult,
            isOpen: false,
        };
    },
    computed: {
        areOptionsObjects() {
            return typeof this.options[0] === 'object';
        },
        showClearOptionsButton() {
            return this.selectValue && this.selectValue.length > 0 && this.multiple;
        },
        chevron() {
            return document.querySelector('.multiselect__select');
        },
    },
    watch: {
        disabled(newValue) {
            if (newValue === this.isDisabled) {
                return;
            }

            this.isDisabled = newValue;
        },
        value(newValue) {
            if (newValue === this.selectValue) {
                return;
            }

            this.selectValue = newValue;

            this.handleChevron();
        },
        isLoading(newValue) {
            if (newValue === this.loading) {
                return;
            }

            this.loading = newValue;
        },
        hasNoResult(newValue) {
            if (newValue === this.isNoResult) {
                return;
            }

            this.isNoResult = newValue;
        },
    },
    methods: {
        handleUpdate(value) {
            // If the "Apply" button is present
            // the update will happen on
            // the "Apply" button click
            if (this.hasApplyButton) {
                return;
            }

            this.$emit('input', value);
        },
        handleApplyChanges() {
            // Setting this flag to `true`
            // when the "Apply" button is clicked.
            // In this case, the multiselect will
            // hold its values after closing.
            // If the "Apply" button is not clicked
            // when closing the multiselect,
            // the values will be reset.
            this.hasAppliedChanges = true;

            // Also emitting the values of the select
            // on the "Apply" button click (for v-model).
            this.$emit('input', this.selectValue);

            // Close the multiselect.
            this.$refs.multiselect.deactivate();

            // Emit a separate event to enable
            //  handling the click on the apply button.
            // `setTimeout` to allow the propagation
            // of the values from the `input` event above.
            setTimeout(() => {
                this.$emit('click-apply', this.selectValue);
            }, 100);
        },
        handleClearAll() {
            // On "Clear all click"
            // reset the values.
            this.selectValue = [];

            // Emmit empty values array (v-model).
            this.$emit('input', []);

            // Same as in the method above,
            // setting this flag in order to
            // hold the empty state on multiselect closing,
            // just in case it was not empty before.
            this.hasAppliedChanges = true;

            // Close the multiselect.
            this.$refs.multiselect.deactivate();
        },
        getCheckedOptionStatus(option) {
            // Verify if an option is checked or not
            // to make the associated
            // checkbox checked or not.

            if (!this.selectValue) {
                return false;
            }

            return this.areOptionsObjects
                ? this.selectValue.findIndex((o) => o.value === option.value) > -1
                : this.selectValue.includes(option);
        },
        handleOnClose() {
            // On multiselect closing,
            // when there's multiple selection
            // and also having the "Apply" button,
            // if the user did not confirm the selection
            // by clicking the "Apply" button or
            // did not clear the selected options
            // reset the selection to the previous
            // options received via the `value` prop.
            if (this.multiple && this.hasApplyButton && !this.hasAppliedChanges) {
                this.selectValue = this.value;
            }

            // Reset the flag for the next
            // opening of the multiselect.
            this.hasAppliedChanges = false;

            if (this.isDealerSelect) {
                this.$emit('close');
            }

            this.isOpen = false;
            this.handleChevron();
        },
        getOptionLabel(option) {
            if (this.areOptionsObjects) {
                return option.label ? option.label : '';
            }

            return option;
        },
        limitText(count) {
            return `+${count}`;
        },
        handleOnOpen() {
            if (this.isDealerSelect) {
                this.$emit('click-open');
            }

            this.isOpen = true;
            this.handleChevron();
        },
        isCurrentDealer(locationId) {
            if (!this.selectValue) {
                return;
            }

            return this.selectValue.locationId === locationId;
        },
        clearOptions() {
            this.selectValue = [];
            this.$emit('input', []);
        },
        handleClick() {
            if (!this.isOpen) {
                this.handleOnOpen();
                return;
            }
            this.$refs.multiselect.deactivate();
        },
        handleChevron() {
            const hasValue = Array.isArray(this.selectValue) ? this.selectValue.length : this.selectValue;
            if (this.chevron && hasValue && !this.isOpen) {
                this.chevron.style.display = 'none';
            } else {
                this.chevron.style.display = 'block';
            }
        },
    },
};
</script>
